import { useState, useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useHistory, useParams, generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Notification, Prompt, Spin, Layout } from 'Components';
import GroupsLayoutTemplate from 'Pages/Groups/GroupsLayoutTemplate';
import { useGroupsHierarchyContext } from './GroupsHierarchyContext';
import { AddMultipleGroupsMenu } from 'Pages/Groups/components';
import {
	HierarchyGroup,
	HierarchyValues,
	RouteParams
} from './GroupsHierarchy.types';
import HierarchyGroupComponent from './HierarchyGroup';
import {
	prepareFormValues,
	createHierarchyInput
} from './GroupsHierarchy.helpers';
import { CREATE_HIERARCHY } from 'Services/Api/Groups/Mutations';
import { GET_GROUPS_LIST } from 'Services/Api/Groups/Queries';
import {
	CreateHierarchyGroupsRequest,
	CreateHierarchyGroupsResponse,
	GetGroupsListResponse
} from 'Services/Api/Groups/Types';
import { getAppRoutes } from 'Pages/App/App.constants';
import { checkApolloError } from 'Helpers/graphql';
import { APIErrorCodes, ErrorInfo } from 'app-types';
import {
	DataTreeContextProps,
	DataTreeNode
} from 'Contexts/DataTree/DataTree.types';
import {
	bulkAssignNestedHierarchyLabel
} from 'Services/Api/HierarchyLabel/HierarchyLabelService';
import { HierarchyLabelType } from 'app-config';
import { apolloClient } from 'Services/Api/client';
import { Group } from 'Services/Api/Groups/Types';

const GroupsHierarchyManageLayout = () => {
	const { t } = useTranslation();
	const [createHierarchy, { loading }] = useMutation<
		CreateHierarchyGroupsResponse,
		CreateHierarchyGroupsRequest
	>(CREATE_HIERARCHY);
	const { hierarchyGroups, blockIsUniqError, parent } = useGroupsHierarchyContext();
	const { groupId } = useParams<RouteParams>();
	const history = useHistory();
	const [selectedItem, setSelectedItem] = useState<DataTreeNode[]>([
		{
			key: (hierarchyGroups[0]?.id as unknown) as number,
			title: hierarchyGroups[0]?.name
		}
	]);

	const [reloadingFieldArray, setReloadingFieldArray] = useState(false);
	const form = useForm({
		defaultValues: prepareFormValues(hierarchyGroups),
		mode: 'all'
	});

	const [{ key, title }] = selectedItem;

	const group = useMemo(
		() =>
			hierarchyGroups.find((group) => group.id === key) as HierarchyGroup,
		[key, hierarchyGroups]
	);

	const handleOnHierarchyGroupSubmit = useCallback(
		async (values: HierarchyValues) => {
			try {
				const input = createHierarchyInput(values, groupId);

				await createHierarchy({
					variables: {
						input
					}
				});

				// Collect hierarchy labels from form values (excluding groups without labels)
				const allFormGroups = Object.values(values).flat();
				const groupsWithLabels = allFormGroups.filter(
					(g) => g.hierarchyLabel && g.name
				);

				if (groupsWithLabels.length > 0) {
					try {
						// Fetch the newly created groups (with real IDs) including nested levels
						const { data: groupsData } = await apolloClient.query<GetGroupsListResponse>({
							query: GET_GROUPS_LIST,
							fetchPolicy: 'network-only',
							variables: {
								filters: {
									parentId: Number(groupId),
									depth: 6
								}
							}
						});

						const createdGroups = groupsData?.listGroups;

						if (createdGroups && createdGroups.length > 0) {
							// Derive rootGroupId from the fetched groups themselves — they
							// already carry the correct rootGroupId from the listGroups query.
							// Fall back to tenantId (from GET_GROUP) or groupId as last resort.
							const rootGroupId =
								createdGroups[0].rootGroupId ??
								(Number(parent?.tenantId) || Number(groupId));

							// ── Build a UUID → real-ID map so we can match nested children ──
							// Form groups use temporary UUIDs for id/parentId.
							// Backend groups use real numeric ids.
							// We process level-by-level: first match level-2 groups (whose
							// parentId is the route groupId), then level-3 groups (whose
							// real parentId we now know from the level-2 mapping), etc.
							const uuidToRealId = new Map<string, number>();
							// The route groupId is the parent for all level-2 form groups
							uuidToRealId.set(String(groupId), Number(groupId));

							// All form groups (including those without labels) — we need
							// them all to build the parent mapping for deeper levels.
							const everyFormGroup = Object.values(values).flat();

							// Sort by level so we process parents before children
							const sortedFormGroups = [...everyFormGroup].sort(
								(a, b) => a.level - b.level
							);

							// Track which backend groups have already been claimed
							// so that two form groups with the same name+level under
							// different parents don't both grab the same backend row.
							const claimedBackendIds = new Set<number>();

							for (const fg of sortedFormGroups) {
								// Resolve the form group's parent UUID to a real ID
								const realParentId = fg.parentId
									? uuidToRealId.get(fg.parentId)
									: Number(groupId);

								// Find the backend group that matches name + level + real parentId
								const match = createdGroups.find(
									(cg: Group) =>
										cg.name === fg.name &&
										cg.level === fg.level &&
										Number(cg.parentId) === realParentId &&
										!claimedBackendIds.has(Number(cg.id))
								);

								if (match) {
									uuidToRealId.set(fg.id, Number(match.id));
									claimedBackendIds.add(Number(match.id));
								}
							}

							// Now build the label assignments using the resolved mapping
							const nodeTypesMap = new Map<number, {
								id: number;
								name: string;
								parentId: number | null;
								rootGroupId: number;
								level: number;
								nodeType: HierarchyLabelType;
							}>();

							for (const fg of sortedFormGroups) {
								if (!fg.hierarchyLabel) continue;
								const realId = uuidToRealId.get(fg.id);
								if (!realId) continue;

								const backendGroup = createdGroups.find(
									(cg: Group) => Number(cg.id) === realId
								);
								if (!backendGroup) continue;

								nodeTypesMap.set(realId, {
									id: realId,
									name: backendGroup.name,
									parentId: backendGroup.parentId,
									rootGroupId: backendGroup.rootGroupId ?? rootGroupId,
									level: backendGroup.level,
									nodeType: fg.hierarchyLabel as HierarchyLabelType
								});
							}

							const nodeTypes = Array.from(nodeTypesMap.values());

							if (nodeTypes.length > 0) {
								const labelResult = await bulkAssignNestedHierarchyLabel(
									{
										rootGroupId,
										nodeTypes
									}
								);

								if (labelResult && !labelResult.success) {
									console.error(
										'Failed to assign hierarchy labels:',
										labelResult.message
									);
									Notification.error({
										description:
											labelResult.message ||
											'Hierarchy was created but labels could not be assigned. Please edit the groups to retry.'
									});
								}
							}
						}
					} catch (labelError) {
						console.error(
							'Failed to assign hierarchy labels:',
							labelError
						);
						Notification.error({
							description:
								'Hierarchy was created but labels could not be assigned. Please edit the groups to retry.'
						});
					}
				}

			Notification.success({
				description: t('groupsHierarchy.successNotification')
			});
			window.unblock?.();
			history.push(
				generatePath(getAppRoutes().GROUPS, { groupId })
			);
			} catch (error) {
				const apolloError = checkApolloError(error);

				if (apolloError.is(APIErrorCodes.UnprocessableEntity)) {
					const metaKeys = apolloError.getMeta<
						Record<string, unknown>
					>();

					if (metaKeys) {
						const keys = Object.keys(metaKeys);

						const valuesLevelsIds = Object.keys(values);

						const pathToTranslation =
							keys.length > 1 ? 'multiple' : 'single';
						Notification.error({
							description: t(
								`groups.errorCodes.${APIErrorCodes.GroupNameShouldBeUniqueAtLevel}.${pathToTranslation}`
							)
						});
						valuesLevelsIds.forEach((levelId) => {
							values[levelId].forEach((value, index) => {
								if (keys.includes(value.id)) {
									form.setError(
										`${levelId}.${index}.name`,
										{}
									);
								}
							});
						});
					}
          return
				}
				if (apolloError.is(APIErrorCodes.GroupCannotBeProperty)) {
					Notification.error({
						description: t(
							`groupsHierarchy.notifications.error.${APIErrorCodes.GroupCannotBeProperty}`
						)
					});
          return
				}

        if(apolloError.is(APIErrorCodes.GroupSyncCodeShouldBeUniqPerTenant)){
					const metaKeys = apolloError.getMeta<
						{rowNumber: number; levelId: string;}[]
					>();

					if (metaKeys?.length) {

						metaKeys.forEach(({levelId, rowNumber}) => {
              form.setError(
                `${levelId}.${rowNumber}.syncCode`,
                {}
              );
						});
					}
          Notification.error({
            description: t('group.fieldErrorCodes.GROUP_SYNC_CODE_SHOULD_BE_UNIQUE_PER_TENANT', {path: 'Group sync code'})
          });

          return 
        }
				if (apolloError.isApolloError()) {
					const errorInfo = apolloError.getErrorInfo<
						Record<string, ErrorInfo[]>
					>();

					if (errorInfo) {
						Notification.error({
							description: t(
								'groupsHierarchy.notifications.error.submitHierarchy'
							)
						});
						return;
					}
					Notification.error({
						description: t('errorCodes.genericErrorMessage')
					});
				}
			}
		},

		// eslint-disable-next-line
		[groupId, history, form]
	);

	const onHierarchyGroupSubmit = useCallback(
		(ev) => {
			form.handleSubmit(handleOnHierarchyGroupSubmit)(ev);
		},
		[form, handleOnHierarchyGroupSubmit]
	);

	const onCancel = useCallback(() => {
		// react-router-dom v5 uses history v4 at runtime (goBack), but
		// the root history v5 types expose back(). Suppress the mismatch.
		// eslint-disable-next-line
		(history as any).goBack();
	}, [history]);

	const onChange: DataTreeContextProps['onChange'] = (value) => {
		if (value) {
			setSelectedItem(value);
			form.reset(form.getValues());
			setReloadingFieldArray(true);
			setTimeout(() => {
				setReloadingFieldArray(false);
			}, 50);
		}
	};

	return (
		<>
			<Prompt blockTransition />
			<FormProvider {...form}>
				<GroupsLayoutTemplate<HierarchyGroup>
					onChange={onChange}
					dataSource={hierarchyGroups}
					initialValue={selectedItem}
					defaultActiveExpand
					header={() => (
						<AddMultipleGroupsMenu
							onCancel={onCancel}
							onSubmit={onHierarchyGroupSubmit}
							loading={loading}
							headerTranslationPath="groupsHierarchy.title"
							disabledSubmit={blockIsUniqError}
						/>
					)}
				>
					{reloadingFieldArray ? (
						<Layout>
							<Spin />
						</Layout>
					) : (
						<HierarchyGroupComponent
							fieldArrayName={String(key)}
							defaultGroupName={title as string}
							group={group}
						/>
					)}
				</GroupsLayoutTemplate>
			</FormProvider>
		</>
	);
};

export default GroupsHierarchyManageLayout;
